import { useState } from "react";
import {
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Box,
  IconButton,
  Stack,
  LinearProgress,
  FormControl,
  FormHelperText,
  Grid,
} from "@mui/material";

import { ArrowBack, ArrowForward } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import assessmentImg from "../../../../../../Image/assessment_img.png";
import ConfirmationDialog from "../../../../Base/views/ConfirmationDialog";
import { toast } from "react-toastify";
const PreScreenAssessment = ({
  formQuestions = [],
  handleConfirmSubmit,
  setCandidateAnswers,
  name,
  candidateData,
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [startAssessment, setStartAssessment] = useState(false);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const maxQuestions = candidateData?.prescreen_random_freq?.prequestion_random_freq;
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const handleStartAssessment = () => {
    const shuffled = shuffleArray(formQuestions);
    const selected = shuffled.slice(0, maxQuestions);
    setAssessmentQuestions(selected);
    setStartAssessment(true);
  };
  /* ---------------- VALIDATION ---------------- */
  
  const validateQuestion = (question, answerData) => {
    const value = answerData?.[question.name];
    if (question.required && (!value || (Array.isArray(value) && value.length === 0))) {
      setErrors((prev) => ({
        ...prev,
        [question.name]: `${question.label} is required`,
      }));

      return false;
    }

    setErrors((prev) => ({
      ...prev,
      [question.name]: "",
    }));

    return true;
  };


  const handleAnswer = (value, question, event = null) => {
    let fieldValue = value;

    if (question.type === "file") {
      const file = event?.target?.files?.[0];

      if (!file) return;

      if (file.size > 2 * 1024 * 1024) {
        toast.error("File size should not exceed 2MB");
        event.target.value = "";
        return;
      }

      fieldValue = file;
    }

    setAnswers((prev) => ({
      ...prev,
      [question.name]: fieldValue,
    }));


    if (question.required) {
      validateQuestion({
        ...question,
        value: fieldValue,
      }, {
        [question.name]: fieldValue,
      });
    }
  };

  const question = assessmentQuestions[currentQuestion];

  const handleNext = () => {
    if (!validateQuestion(question, answers)) return;

    setCurrentQuestion((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentQuestion((prev) => prev - 1);
  };

  const handleSubmit = () => {
    if (!validateQuestion(question, answers)) return;
    setCandidateAnswers(answers);
    setOpenConfirmDialog(true);
  };

  /* ---------------- RENDER QUESTION ---------------- */

  const renderQuestion = () => {
    switch (question?.type) {
      case "text":
        return (
          <FormControl fullWidth error={!!errors[question.name]}>
            <TextField
              multiline
              rows={5}
              value={answers[question.name] || ""}
              onChange={(e) => handleAnswer(e.target.value, question)}
              placeholder="Type your answer..."
            />

            <FormHelperText>{errors[question.name]}</FormHelperText>
          </FormControl>
        );

      case "radio":
        return (
          <FormControl fullWidth error={!!errors[question.name]}>
              <RadioGroup
                value={answers[question.name] || ""}
                onChange={(e) => handleAnswer(e.target.value, question)}
              >
                {question?.options?.map((opt) => (
                  <FormControlLabel
                    key={opt?.value}
                    value={opt?.value}
                    control={<Radio />}
                    label={opt.label}
                  />
                ))}
              </RadioGroup>
            <FormHelperText>{errors[question.name]}</FormHelperText>
          </FormControl>
        );
        case "file":
        return (
          <FormControl fullWidth error={!!errors[question.name]}>
            <TextField
              key={question.name}
              type="file"
              inputProps={{
                accept: "*",
              }}
              onChange={(e) => handleAnswer(null, question, e)}
            />
            {answers[question.name] && (
              <Typography variant="body2">
                Selected: {answers[question.name].name}
              </Typography>
            )}
            <FormHelperText>
              {errors[question.name]}
            </FormHelperText>
          </FormControl>
        );

      default:
        return null;
    }
  };

  const progress = ((currentQuestion + 1) / assessmentQuestions.length) * 100;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              p: { xs: 2, md: 3 }, 
            }}
          >
            {!startAssessment ? (
              <Stack spacing={2} sx={{ alignItems: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "rgba(139, 68, 255, 0.12)",
                      borderRadius: "50%",
                      height: { xs: 60, sm: 80, md: 100 },
                      width: { xs: 60, sm: 80, md: 100 },
                    }}
                  >
                    <CheckCircleIcon
                      color="primary"
                      sx={{
                        fontSize: { xs: 32, sm: 40, md: 50 },
                      }}
                    />
                  </Box>
                </Box>
                <Typography sx={{ fontSize: "14px" }}>Hi, {name}!</Typography>
                <Typography
                  sx={{ fontSize: { xs: "18px", sm: "24px", md: "28px" } }}
                  variant="h3"
                  mt={4}
                  mx={6}
                >
                {candidateData?.isEmail ? "Pre-Screening Assessment" : "Thank you for the provided information"}  
                </Typography>
                <Typography
                  sx={{ fontSize: { xs: "12px", sm: "14px", md: "16px" } }}
                  variant="body3"
                >
                  What's next?
                </Typography>
                <Typography
                  variant="body6"
                  sx={{ fontSize: { xs: "10px", sm: "12px", md: "14px" } }}
                >
                  Please answer a few questions to help us better understand you
                  and assess your proficiency.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleStartAssessment}
                  className="ext1-mpl-btn"
                  sx={{
                    py: 1,
                    fontSize: { xs: "12px", sm: "14px", md: "18px" },
                    width: "50%",
                    minWidth: 250,
                    borderRadius: "10px",
                    textTransform: "none",
                  }}
                >
                  Start Now
                </Button>
              </Stack>
            ) : (
              <Grid container spacing={{ xs: 3, md: 6 }}>
                {/* LEFT SIDE */}
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", md: "flex-start" },
                    order: { xs: 2, md: 1 },
                  }}
                >
                  {/* Image */}
                  <Box
                    sx={{
                      display: { xs: "none", md: "block" }, // hide on xs
                      width: "100%",
                      maxWidth: 280,
                    }}
                  >
                    <Box
                      component="img"
                      src={assessmentImg}
                      alt="Assessment"
                      sx={{
                        width: "100%", // dynamic %
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                  {/* Progress */}

                  {/* Navigation */}
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: { xs: "100%", md: 320 },
                      mt: { xs: 3, md: 0 },
                    }}
                  >
                    {/* Progress */}
                    <Box px={1}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 8,
                          borderRadius: 5,
                          backgroundColor: "#e5e7eb",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 5,
                          },
                        }}
                      />
                    </Box>
                    {/* Navigation */}
                    <Box
                      mt={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <IconButton
                        onClick={handlePrevious}
                        disabled={currentQuestion === 0}
                        sx={{ border: "1px solid #e5e7eb" }}
                      >
                        <ArrowBack />
                      </IconButton>

                      {currentQuestion === assessmentQuestions?.length - 1 ? (
                        <Button
                          variant="contained"
                          className="ext1-mpl-btn"
                          onClick={handleSubmit}
                        >
                          Submit
                        </Button>
                      ) : (
                        <IconButton
                          onClick={handleNext}
                          sx={{ border: "1px solid #e5e7eb" }}
                        >
                          <ArrowForward />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* RIGHT SIDE */}
                <Grid
                  item
                  xs={12}
                  md={8}
                  sx={{
                    order: { xs: 1, md: 2 }, // question first on mobile
                  }}
                >
                  {/* Header */}
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    mb={3}
                    gap={1}
                  >
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      sx={{
                        fontSize: { xs: 24, md: 32 },
                      }}
                    >
                      Pre-Screening Assessment
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      Question {currentQuestion + 1} of {assessmentQuestions.length}
                    </Typography>
                  </Box>

                  {/* Question */}
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    mb={2}
                    sx={{
                      fontSize: { xs: 14, md: 18 },
                      fontWeight: { xs: 600, md: 700 },
                    }}
                  >
                    {question?.label}
                  </Typography>

                  {/* Answer Label */}
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Answer{question?.required ? " *" : ""}
                  </Typography>

                  {/* Input */}
                  <Box maxWidth={700}>{renderQuestion()}</Box>
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>
      </Box>

      {/* CONFIRM DIALOG */}
      <ConfirmationDialog
        title="Confirm Submission"
        content={"Are you sure you want to submit your answers?"}
        openDialog={openConfirmDialog}
        closeDialog={setOpenConfirmDialog}
        OkButtonText="Yes"
        onSubmit={() =>handleConfirmSubmit(answers)}
      />
    </Box>
  );
};

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
export default PreScreenAssessment;
